import { getActiveAccountId } from '@auw/api-client';
import { activeTenantKey as tenantKey } from '@auw/tenant';

export type PersistedOutboxEntry = {
  id: string;
  payload: string;
  retries: number;
  lastSentAt: number;
  retryDelayMs?: number;
  trackSentMetric: boolean;
  updatedAt: number;
};

/* Resolved lazily so this module body can evaluate before the host
 * has registered its tenant manifest. */
let _dbName: string | null = null;
function getDbName(): string {
  if (_dbName === null) _dbName = tenantKey('dm_ws');
  return _dbName;
}

const DB_VERSION = 1;
const STORE_NAME = 'outbox';

function hasIndexedDb(): boolean {
  return typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';
}

function getPersistedOutboxAccountId(): string {
  return getActiveAccountId() || 'anonymous';
}

function getScopedOutboxEntryId(id: string): string {
  return `${getPersistedOutboxAccountId()}:${id}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = window.indexedDB.open(getDbName(), DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => Promise<T>,
): Promise<T> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const result = await work(store);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
    return result;
  } finally {
    db.close();
  }
}

export async function loadPersistedOutboxEntries(limit = 300): Promise<PersistedOutboxEntry[]> {
  if (!hasIndexedDb()) return [];
  try {
    return await withStore('readonly', async (store) => {
      const rows = await new Promise<PersistedOutboxEntry[]>((resolve, reject) => {
        const req = store.getAll();
        req.onsuccess = () => resolve((req.result || []) as PersistedOutboxEntry[]);
        req.onerror = () => reject(req.error);
      });
      const prefix = `${getPersistedOutboxAccountId()}:`;
      const scopedRows = rows
        .filter((row) => typeof row.id === 'string' && row.id.startsWith(prefix))
        .map((row) => ({
          ...row,
          id: row.id.slice(prefix.length),
        }))
        .sort((a, b) => (a.updatedAt || 0) - (b.updatedAt || 0));
      if (scopedRows.length <= limit) return scopedRows;
      return scopedRows.slice(scopedRows.length - limit);
    });
  } catch {
    return [];
  }
}

export async function upsertPersistedOutboxEntry(entry: PersistedOutboxEntry): Promise<void> {
  if (!hasIndexedDb()) return;
  try {
    await withStore('readwrite', async (store) => {
      await new Promise<void>((resolve, reject) => {
        const req = store.put({ ...entry, id: getScopedOutboxEntryId(entry.id) });
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    });
  } catch {
    // Ignore persistence failures; memory outbox still works.
  }
}

export async function removePersistedOutboxEntry(id: string): Promise<void> {
  if (!hasIndexedDb()) return;
  try {
    await withStore('readwrite', async (store) => {
      await new Promise<void>((resolve, reject) => {
        const req = store.delete(getScopedOutboxEntryId(id));
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    });
  } catch {
    // Ignore persistence failures.
  }
}

export async function prunePersistedOutbox(limit = 300): Promise<void> {
  if (!hasIndexedDb()) return;
  try {
    await withStore('readwrite', async (store) => {
      const rows = await new Promise<PersistedOutboxEntry[]>((resolve, reject) => {
        const req = store.getAll();
        req.onsuccess = () => resolve((req.result || []) as PersistedOutboxEntry[]);
        req.onerror = () => reject(req.error);
      });
      const prefix = `${getPersistedOutboxAccountId()}:`;
      const scopedRows = rows
        .filter((row) => typeof row.id === 'string' && row.id.startsWith(prefix))
        .sort((a, b) => (a.updatedAt || 0) - (b.updatedAt || 0));
      if (scopedRows.length <= limit) return;
      const overflow = scopedRows.length - limit;
      for (let i = 0; i < overflow; i++) {
        const key = scopedRows[i]?.id;
        if (!key) continue;
        await new Promise<void>((resolve, reject) => {
          const req = store.delete(key);
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error);
        });
      }
    });
  } catch {
    // Ignore persistence failures.
  }
}
